import { Injectable, signal } from '@angular/core';

import { combineLatest, take } from 'rxjs';

import { CoverageNotificationService } from '@sondermind/features/account-edit';
import { ProviderHttpService } from '@sondermind/data-access/client-portal-referrals';

import { CoverageBadgeType, CoverageInfo } from './coverage-badge-types';

@Injectable({
  providedIn: 'root'
})
export class CoverageBadgeStore {
  private readonly converageStatusStore = signal<Record<number, CoverageInfo>>({});
  private readonly loadingProviders = signal<Set<number>>(new Set());

  private readonly providerPNCInfo = signal<Record<number, number[]>>({});

  constructor(
    private coverageNotificationService: CoverageNotificationService,
    private providerHttpService: ProviderHttpService
  ) {}

  getCoverageInfo(providerId: number): CoverageInfo | undefined {
    return this.converageStatusStore()[providerId];
  }

  isUpdating(providerId?: number): boolean {
    if (providerId) {
      return this.loadingProviders().has(providerId);
    }
    return this.loadingProviders().size > 0;
  }

  loadCoverageStatusInfo(personaProviderIds: number[]): void {
    // no ids passed in
    if (personaProviderIds.length === 0) return;

    const idsToLoad = personaProviderIds
      // filter being loaded
      .filter((id) => !this.loadingProviders().has(id))
      // filter already loaded
      .filter((id) => !(id in this.converageStatusStore()));

    // no new ids to load
    if (idsToLoad.length === 0) return;

    // Mark as loading
    this.loadingProviders.update((ids) => new Set([...ids, ...idsToLoad]));

    // call checkCoverageNotification() to make sure we have user's insurances
    // note: the call is a "no-op" if we already have checked insurances, so no harm re-calling
    this.coverageNotificationService.checkCoverageNotification();

    // grab client insurance info
    combineLatest({
      hasInactiveInsurance: this.coverageNotificationService.hasInactiveInsurance$,
      hasExpiredInsurance: this.coverageNotificationService.hasExpiredInsurance$,
      hasUnverifiedInsurance: this.coverageNotificationService.hasUnverifiedInsurance$,
      hasValidInsurance: this.coverageNotificationService.hasValidInsurance$,
      validInsurances: this.coverageNotificationService.validInsurances$,
      skipEarlyBvInsurances: this.coverageNotificationService.skipEarlyBvInsurances$,
      pncInfo: this.providerHttpService.getPNCInfo$(idsToLoad, { page: 'disabled' }).pipe(take(1))
    })
      .subscribe(({
        hasInactiveInsurance, hasExpiredInsurance, hasUnverifiedInsurance,
        hasValidInsurance, validInsurances, skipEarlyBvInsurances, pncInfo
      }) => {
        // for each provider in this batch,
        // 1. determine badge type
        // 2. update coverage status cache
        // 3. remove from the loading list
        pncInfo.forEach((info) => {
          // default to self-pay
          let badgeType = CoverageBadgeType.SELF_PAY;

          // check if in-network by looking at the valid insurances and the skip early BV insurances
          const inNetworkInsurance = [...validInsurances, ...skipEarlyBvInsurances].find(
            (insurance) => info.pncIds.includes(insurance.providerNetworkCompany?.id ?? 0)
          );

          if (inNetworkInsurance) {
            badgeType = CoverageBadgeType.IN_NETWORK;
          } else if (hasValidInsurance) {
            badgeType = CoverageBadgeType.OUT_OF_NETWORK;
          } else if (hasInactiveInsurance) {
            badgeType = CoverageBadgeType.COVERAGE_NOT_STARTED;
          } else if (hasUnverifiedInsurance) {
            badgeType = CoverageBadgeType.COVERAGE_UNVERIFIED;
          } else if (hasExpiredInsurance) {
            badgeType = CoverageBadgeType.COVERAGE_ENDED;
          }

          const coverageInfo = {
            badgeType,
            insuranceName: inNetworkInsurance?.providerNetworkCompany?.name ?? '',
            isSelfPay: badgeType === CoverageBadgeType.SELF_PAY,
          };

          // update the cache
          this.converageStatusStore.update((currentStore) => (
            { ...currentStore, [info.personaProviderId]: coverageInfo }
          ));

          // remove from loading set
          this.loadingProviders.update((providers) => {
            const newProviders = new Set(providers);
            newProviders.delete(info.personaProviderId);
            return newProviders;
          });
        });
      });
  }
}
